import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Client from "shopify-buy";
import { useMarket } from "./MarketContext.jsx";

// Utility: base64 in browser or Node
const toBase64 = (s) => {
  if (typeof btoa === "function") return btoa(s); // browser
  if (typeof Buffer !== "undefined")
    return Buffer.from(s).toString("base64"); // Node/SSR
  return s;
};

// Utility function to construct the global ID (base64-encoded GID)
const constructCollectionId = (numericId) =>
  toBase64(`gid://shopify/Collection/${numericId}`);

// Map filter keys to collection numeric IDs
const collectionIdMap = {
  bras: ["459578573117"],
  knickers: ["673627898237"],
  swim: ["459576869181"],
  clothing: ["465259266365"],
  accessories: ["677295784317"],
  personalised: ["459893539133"],
};

// Shop All collection (from your admin URL)
const SHOP_ALL_NUMERIC_ID = "459894325565";
const SHOP_ALL_GID = constructCollectionId(SHOP_ALL_NUMERIC_ID);

const ShopContext = React.createContext();

const client = Client.buildClient({
  storefrontAccessToken: "64d3395ffdbadbcad4398c2e893d8a76",
  domain: "b3deb6-2.myshopify.com",
});

/**
 * Ensure we provide a valid CountryCode-like 2-letter ISO for injection into @inContext.
 * This returns an uppercase 2-letter code or falls back to 'GB'.
 * NOTE: we inject this into the GraphQL template as an unquoted enum (e.g. US, GB).
 */
const safeCountryEnum = (c) => {
  const s = (c || "GB").toString().trim().toUpperCase();
  return /^[A-Z]{2}$/.test(s) ? s : "GB";
};

const ShopProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState({});
  const [collections, setCollections] = useState([]);
  const [cart, setCart] = useState(null); // Replace `checkout` with `cart`
  const [cartCount, setCartCount] = useState(0);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Read market so provider re-renders when market changes (additive only)
  const { market } = useMarket();

  // used to avoid double-refetch on initial mount
  const hasInitializedCart = useRef(false);
  // avoid parallel refreshes
  const isRefreshingCart = useRef(false);

  // Create or fetch the cart (run once on mount)
  useEffect(() => {
    const initializeCart = async () => {
      try {
        let cartId = localStorage.getItem("shopify_cart_id");

        if (cartId) {
          const existingCart = await fetchCart(cartId, market?.country);
          if (existingCart) {
            setCart(existingCart);
            hasInitializedCart.current = true;
            return;
          }
        }

        // If no cart exists, create a new one (create in current market)
        const newCart = await createCart(market?.country);
        if (newCart) {
          localStorage.setItem("shopify_cart_id", newCart.id);
          setCart(newCart);
        }
        hasInitializedCart.current = true;
      } catch (error) {
        console.error("Error initializing cart:", error);
        hasInitializedCart.current = true;
      }
    };

    initializeCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // If user switches market, create a fresh cart in that market
  useEffect(() => {
    if (!hasInitializedCart.current) return;

    const resetCartForMarket = async () => {
      try {
        // Drop any existing cart ID so Shopify can price in the new market
        localStorage.removeItem("shopify_cart_id");

        const newCart = await createCart(market?.country);
        if (newCart) {
          setCart(newCart);
        } else {
          setCart(null);
        }
      } catch (err) {
        console.error("Error resetting cart for new market:", err);
      }
    };

    resetCartForMarket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market?.country]);

  // Helper: fetch cart and update state (exposed)
  const refreshCart = useCallback(
    async (cartId) => {
      if (!cartId) return null;
      const refreshed = await fetchCart(cartId, market?.country);
      if (refreshed) setCart(refreshed);
      return refreshed;
    },
    [market?.country],
  );

  // ---- Core GraphQL helpers (memoized to avoid changing identity) ----

  // Fetch an existing cart (now accepts optional `country` ISO2 code)
  const fetchCart = useCallback(
    async (cartId, country = market?.country ?? "GB") => {
      try {
        const ctxCountry = safeCountryEnum(country);

        const response = await fetch(
          `https://${client.config.domain}/api/2024-07/graphql`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                client.config.storefrontAccessToken,
            },
            body: JSON.stringify({
              // Variables first, then directive — inject enum literal (no quotes)
              query: `
                query ($cartId: ID!) @inContext(country: ${ctxCountry}) {
                  cart(id: $cartId) {
                    id
                    checkoutUrl

                    # Cart totals in current market
                    cost {
                      subtotalAmount {
                        amount
                        currencyCode
                      }
                      totalAmount {
                        amount
                        currencyCode
                      }
                    }

                    lines(first: 100) {
                      edges {
                        node {
                          id
                          quantity

                          # Line cost in current market
                          cost {
                            amountPerQuantity {
                              amount
                              currencyCode
                            }
                            totalAmount {
                              amount
                              currencyCode
                            }
                          }

                          merchandise {
                            ... on ProductVariant {
                              id
                              title
                              product {
                                title
                              }
                              price {
                                amount
                                currencyCode
                              }
                              selectedOptions {
                                name
                                value
                              }
                            }
                          }
                          attributes {
                            key
                            value
                          }
                        }
                      }
                    }
                  }
                }
              `,
              variables: { cartId },
            }),
          },
        );

        const json = await response.json();
        const fetchedCart = json.data?.cart || null;

        if (fetchedCart) {
          setCart(fetchedCart);
          localStorage.setItem("shopify_cart_id", fetchedCart.id); // Ensure cart ID is always saved
        } else {
          console.warn("Cart not found, creating a new one...");
          const newCart = await createCart(country);
          if (newCart) {
            setCart(newCart);
            localStorage.setItem("shopify_cart_id", newCart.id);
          }
        }

        return fetchedCart;
      } catch (error) {
        console.error("Error fetching cart:", error);
        return null;
      }
    },
    [market?.country],
  );

  // Create a new cart (optionally create in a specific country)
  const createCart = useCallback(
    async (country = market?.country ?? "GB") => {
      try {
        const ctxCountry = safeCountryEnum(country);

        const response = await fetch(
          `https://${client.config.domain}/api/2024-07/graphql`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                client.config.storefrontAccessToken,
            },
            body: JSON.stringify({
              // directive on the operation — inject enum literal (no quotes)
              query: `
                mutation @inContext(country: ${ctxCountry}) {
                  cartCreate {
                    cart {
                      id
                      checkoutUrl
                    }
                  }
                }
              `,
            }),
          },
        );

        const json = await response.json();
        const newCart = json.data?.cartCreate?.cart || null;

        if (newCart) {
          localStorage.setItem("shopify_cart_id", newCart.id);
        }

        return newCart;
      } catch (error) {
        console.error("Error creating cart:", error);
        return null;
      }
    },
    [market?.country],
  );

  // Add items to the cart
  const addItemsToCart = useCallback(
    async (variantId, quantity, customAttributes = [], meta = {}) => {  
      try {
        const ctxCountry = safeCountryEnum(market?.country);

        if (!cart?.id) {
          console.error("Cart is not initialized. Creating a new cart...");
          const newCart = await createCart(market?.country);
          if (newCart) {
            localStorage.setItem("shopify_cart_id", newCart.id);
            setCart(newCart);
          } else {
            console.error("Failed to create a cart.");
            return;
          }
        }

        // Ensure cart ID is available
        const cartId = cart?.id || localStorage.getItem("shopify_cart_id");
        if (!cartId) {
          console.error("Cart ID is missing, cannot proceed.");
          return;
        }

        // Filter out empty attributes
        const filteredAttributes = customAttributes.filter(
          (attr) => (attr?.value || "").toString().trim() !== "",
        );

        console.log("Adding item to cart:", {
          cartId,
          variantId,
          quantity,
          customAttributes: filteredAttributes,
          country: ctxCountry,
        });

        const response = await fetch(
          `https://${client.config.domain}/api/2024-07/graphql`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                client.config.storefrontAccessToken,
            },
            body: JSON.stringify({
              query: `
                mutation($cartId: ID!, $lines: [CartLineInput!]!) @inContext(country: ${ctxCountry}) {
                  cartLinesAdd(cartId: $cartId, lines: $lines) {
                    cart {
                      id
                      checkoutUrl
                      lines(first: 100) {
                        edges {
                          node {
                            id
                            quantity
                            merchandise {
                              ... on ProductVariant {
                                id
                                title
                                product {
                                  title
                                }
                                price {
                                  amount
                                  currencyCode
                                }
                                selectedOptions {
                                  name
                                  value
                                }
                              }
                            }
                            attributes {
                              key
                              value
                            }
                          }
                        }
                      }
                    }
                    userErrors {
                      field
                      message
                    }
                  }
                }
              `,
              variables: {
                cartId,
                lines: [
                  {
                    merchandiseId: variantId,
                    quantity: parseInt(quantity, 10),
                    attributes: filteredAttributes,
                  },
                ],
              },
            }),
          },
        );

        const json = await response.json();
        console.log("Shopify API Response:", json);

        // Check for errors in the API response
        if (json.data?.cartLinesAdd?.userErrors?.length > 0) {
          console.error("Shopify API Error:", json.data.cartLinesAdd.userErrors);
          return;
        }

        // Update cart state
        const updatedCart = json.data?.cartLinesAdd?.cart;
        if (updatedCart) {
          setCart(updatedCart);
          await refreshCart(updatedCart.id);
        
          // Meta AddToCart event (browser-side)
          if (typeof window !== "undefined" && typeof window.fbq === "function") {
            const value = Number(meta.value || 0);
            const currency = meta.currency || "GBP";
        
            window.fbq("track", "AddToCart", {
              content_ids: meta.content_ids || [],
              content_type: "product",
              value: Number.isFinite(value) ? value : 0,
              currency,
            });
          }
        
          console.log("Cart updated successfully:", updatedCart);
        } else {
          console.error("Failed to update cart. No cart data returned.");
        }
      } catch (error) {
        console.error("Error adding items to cart:", error);
      }
    },
    [cart?.id, createCart, market?.country, refreshCart],
  );

  // Update item quantity
  const updateItemQuantity = useCallback(
    async (lineItemId, quantity) => {
      try {
        const ctxCountry = safeCountryEnum(market?.country);
        let cartId = cart?.id || localStorage.getItem("shopify_cart_id");
        if (!cartId) {
          console.error("Cart ID is missing, cannot update quantity.");
          return;
        }

        const response = await fetch(
          `https://${client.config.domain}/api/2024-07/graphql`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                client.config.storefrontAccessToken,
            },
            body: JSON.stringify({
              query: `
                mutation($cartId: ID!, $lines: [CartLineUpdateInput!]!) @inContext(country: ${ctxCountry}) {
                  cartLinesUpdate(cartId: $cartId, lines: $lines) {
                    cart {
                      id
                      checkoutUrl
                      lines(first: 100) {
                        edges {
                          node {
                            id
                            quantity
                            merchandise {
                              ... on ProductVariant {
                                id
                                title
                                product {
                                  title
                                }
                                price {
                                  amount
                                  currencyCode
                                }
                                selectedOptions {
                                  name
                                  value
                                }
                              }
                            }
                            attributes {
                              key
                              value
                            }
                          }
                        }
                      }
                    }
                  }
                }
              `,
              variables: {
                cartId,
                lines: [{ id: lineItemId, quantity: parseInt(quantity, 10) }],
              },
            }),
          },
        );

        const json = await response.json();
        const updatedCart = json.data?.cartLinesUpdate?.cart;

        if (updatedCart) {
          setCart(updatedCart);
          // re-fetch to ensure currency is correct for current market
          await refreshCart(updatedCart.id);
          console.log("Updated cart quantity:", updatedCart);
        } else {
          console.error("Failed to update cart quantity.");
        }
      } catch (error) {
        console.error("Error updating item quantity:", error);
      }
    },
    [cart?.id, market?.country, refreshCart],
  );

  // Remove items from cart
  const removeItemFromCart = useCallback(
    async (lineItemId) => {
      try {
        const ctxCountry = safeCountryEnum(market?.country);
        let cartId = cart?.id || localStorage.getItem("shopify_cart_id");
        if (!cartId) {
          console.error("Cart ID is missing, cannot remove item.");
          return;
        }

        const response = await fetch(
          `https://${client.config.domain}/api/2024-07/graphql`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                client.config.storefrontAccessToken,
            },
            body: JSON.stringify({
              query: `
                mutation($cartId: ID!, $lineItemIds: [ID!]!) @inContext(country: ${ctxCountry}) {
                  cartLinesRemove(cartId: $cartId, lineIds: $lineItemIds) {
                    cart {
                      id
                      checkoutUrl
                      lines(first: 100) {
                        edges {
                          node {
                            id
                            quantity
                            merchandise {
                              ... on ProductVariant {
                                id
                                title
                                product {
                                  title
                                }
                                price {
                                  amount
                                  currencyCode
                                }
                              }
                            }
                            attributes {
                              key
                              value
                            }
                          }
                        }
                      }
                    }
                  }
                }
              `,
              variables: {
                cartId,
                lineItemIds: [lineItemId],
              },
            }),
          },
        );

        const json = await response.json();
        const updatedCart = json.data?.cartLinesRemove?.cart;

        if (updatedCart) {
          setCart(updatedCart);
          // refresh to guarantee currency/prices come from correct market context
          await refreshCart(updatedCart.id);
          console.log("Item removed successfully:", updatedCart);
        } else {
          console.error("Failed to remove item from cart.");
        }
      } catch (error) {
        console.error("Error removing item from cart:", error);
      }
    },
    [cart?.id, market?.country, refreshCart],
  );

  // const fetchAllProducts = fetchAllProductsGraphQL;
  const fetchAllProducts = useCallback(async () => {
    const allProducts = [];
    let hasNextPage = true;
    let endCursor = null;

    while (hasNextPage) {
      const query = `
        query ($id: ID!, $cursor: String) {
          collection(id: $id) {
            products(first: 20, after: $cursor, sortKey: COLLECTION_DEFAULT) {
              edges {
                node {
                  id
                  handle
                  title
                  description
                  availableForSale
                  images(first: 2) {
                    edges {
                      node {
                        url(transform: { preferredContentType: WEBP, maxWidth: 1200 })
                        width
                        height
                      }
                    }
                  }
                  variants(first: 1) {
                    edges {
                      node {
                        id
                        availableForSale
                        price { amount currencyCode }
                        compareAtPrice { amount currencyCode }
                      }
                    }
                  }
                }
              }
              pageInfo { hasNextPage endCursor }
            }
          }
        }
      `;

      const variables = { id: SHOP_ALL_GID, cursor: endCursor };

      const response = await fetch(
        `https://${client.config.domain}/api/2024-07/graphql`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token":
              client.config.storefrontAccessToken,
          },
          body: JSON.stringify({ query, variables }),
        },
      );

      const json = await response.json();
      const conn = json?.data?.collection?.products;
      if (!conn) {
        console.warn("Shop All collection not found.");
        return [];
      }

      allProducts.push(...conn.edges.map((e) => e.node));
      hasNextPage = conn.pageInfo.hasNextPage;
      endCursor = conn.pageInfo.endCursor;
    }

    return allProducts;
  }, []);

  // 🚀 UPDATED: fetch by ID (legacy) OR by handle (SEO URLs)
  const fetchProductWithId = useCallback(
    async (idOrGid, country = market?.country ?? "GB", options = {}) => {
      try {
        const ctxCountry = safeCountryEnum(country);

        const productFields = `
          id
          handle
          title
          description
          descriptionHtml
          availableForSale
          images(first: 50) {
            edges { node {
              url(transform: { preferredContentType: WEBP, maxWidth: 1800 })
              width
              height
            } }
          }
          options { name values }
          variants(first: 100) {
            edges {
              node {
                id
                title
                availableForSale
                selectedOptions { name value }
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
              }
            }
          }

          # Related products metafield: list.product_reference
          metafield(namespace: "custom", key: "related_products") {
            references(first: 10) {
              edges {
                node {
                  ... on Product {
                    id
                    handle
                    title
                    images(first: 2) {
                      edges { node {
                        url(transform: { preferredContentType: WEBP, maxWidth: 1200 })
                        width
                        height
                      } }
                    }
                    variants(first: 1) {
                      edges {
                        node {
                          id
                          price { amount currencyCode }
                          compareAtPrice { amount currencyCode }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        let query;
        let variables;
        let dataKey;

        if (options.mode === "handle" && options.handle) {
          // Fetch by handle for SEO URLs
          query = `
            query ($handle: String!) @inContext(country: ${ctxCountry}) {
              productByHandle(handle: $handle) {
                ${productFields}
              }
            }
          `;
          variables = { handle: options.handle };
          dataKey = "productByHandle";
        } else {
          // Legacy: fetch by ID/GID
          query = `
            query ($id: ID!) @inContext(country: ${ctxCountry}) {
              product(id: $id) {
                ${productFields}
              }
            }
          `;
          variables = { id: idOrGid };
          dataKey = "product";
        }

        const response = await fetch(
          `https://${client.config.domain}/api/2024-07/graphql`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                client.config.storefrontAccessToken,
            },
            body: JSON.stringify({ query, variables }),
          },
        );

        const json = await response.json();
        const p = json?.data?.[dataKey];
        if (!p) return null;

        // Build related products from metafield references
        const relatedEdges = p.metafield?.references?.edges || [];
        const relatedProducts = relatedEdges
          .map((e) => e?.node)
          .filter(Boolean)
          .map((r) => ({
            id: r.id,
            handle: r.handle,
            title: r.title,
            images: (r.images?.edges || []).map((imgE) => imgE.node),
            variants: (r.variants?.edges || []).map((vE) => ({
              id: vE.node.id,
              price: vE.node.price,
              compareAtPrice: vE.node.compareAtPrice,
            })),
          }));

        const normalized = {
          id: p.id,
          handle: p.handle,
          title: p.title,
          description: p.description,
          descriptionHtml: p.descriptionHtml,
          availableForSale: p.availableForSale,
          images: (p.images?.edges || []).map((e) => e.node),
          options: (p.options || []).map((o) => ({
            name: o.name,
            values: (o.values || []).map((v) => ({ value: v })),
          })),
          variants: (p.variants?.edges || []).map((e) => ({
            id: e.node.id,
            title: e.node.title,
            selectedOptions: e.node.selectedOptions,
            price: e.node.price,
            compareAtPrice: e.node.compareAtPrice,
            availableForSale: e.node.availableForSale,
          })),
          relatedProducts, // includes handle now
        };

        setProduct(normalized);
        return normalized;
      } catch (error) {
        console.error("Error fetching product:", error);
        return null;
      }
    },
    [market?.country],
  );

  const fetchProductsByType = useCallback(async (filterKey) => {
    const numericIds = collectionIdMap[filterKey.toLowerCase()];
    if (!numericIds || numericIds.length === 0) {
      throw new Error(`Invalid filter key: ${filterKey}`);
    }

    const allFilteredProducts = [];

    for (const numericId of numericIds) {
      const collectionId = constructCollectionId(numericId);
      let hasNextPage = true;
      let endCursor = null;

      while (hasNextPage) {
        const query = `
          query($collectionId: ID!, $cursor: String) {
            collection(id: $collectionId) {
              products(first: 20, after: $cursor) {
                edges {
                  node {
                    id
                    handle
                    title
                    description
                    availableForSale
                    images(first: 2) {
                      edges {
                        node {
                          url(transform: { preferredContentType: WEBP, maxWidth: 1200 })
                          width
                          height
                        }
                      }
                    }
                    variants(first: 1) {
                      edges {
                        node {
                          id
                          availableForSale
                          price { amount currencyCode }
                          compareAtPrice { amount currencyCode }
                        }
                      }
                    }
                  }
                }
                pageInfo { hasNextPage endCursor }
              }
            }
          }
        `;

        const response = await fetch(
          `https://${client.config.domain}/api/2024-07/graphql`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                client.config.storefrontAccessToken,
            },
            body: JSON.stringify({
              query,
              variables: { collectionId, cursor: endCursor },
            }),
          },
        );

        const json = await response.json();
        const products = json.data.collection.products.edges.map(
          (edge) => edge.node,
        );
        allFilteredProducts.push(...products);

        hasNextPage = json.data.collection.products.pageInfo.hasNextPage;
        endCursor = json.data.collection.products.pageInfo.endCursor;
      }
    }

    return allFilteredProducts;
  }, []);

  const searchProducts = useCallback(async (searchTerm) => {
    try {
      const clean = (searchTerm || "").trim();
      if (!clean) {
        return [];
      }
  
      console.log("Searching for products with term:", clean);
  
      // STRICT Shopify search: only look in title and tags
      const shopifyQuery = `(title:${clean}* OR tag:${clean})`;
  
      const searchQuery = `
        query($query: String!) {
          products(first: 40, query: $query) {
            edges {
              node {
                id
                handle
                title
                description
                images(first: 1) {
                  edges { node { src } }
                }
                variants(first: 1) {
                  edges {
                    node {
                      id
                      price { amount currencyCode }
                      compareAtPrice { amount currencyCode }
                    }
                  }
                }
              }
            }
          }
        }
      `;
  
      const response = await fetch(
        `https://${client.config.domain}/api/2024-07/graphql`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": client.config.storefrontAccessToken,
          },
          body: JSON.stringify({
            query: searchQuery,
            variables: { query: shopifyQuery },
          }),
        }
      );
  
      const json = await response.json();
      console.log("Search API Response:", json);
  
      if (json.data?.products?.edges?.length > 0) {
        return json.data.products.edges.map((edge) => edge.node);
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error searching products:", error);
      return [];
    }
  }, []);  

  const getCheckoutUrl = useCallback(() => {
    return cart?.checkoutUrl || "";
  }, [cart?.checkoutUrl]);

  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  const openCart = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  const fetchProductByVariantId = useCallback(async (variantId) => {
    try {
      const response = await fetch(
        `https://${client.config.domain}/api/2024-07/graphql`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token":
              client.config.storefrontAccessToken,
          },
          body: JSON.stringify({
            query: `
              query getProductByVariant($variantId: ID!) {
                node(id: $variantId) {
                  ... on ProductVariant {
                    product {
                      id
                    }
                  }
                }
              }
            `,
            variables: { variantId },
          }),
        },
      );

      const json = await response.json();
      const productId = json.data?.node?.product?.id; // ✅ Correct path

      if (productId) {
        return productId;
      } else {
        console.error("Product ID not found for variant:", variantId);
        return null;
      }
    } catch (error) {
      console.error("Error fetching product by variant ID:", error);
      return null;
    }
  }, []);

  // Combine all state variables and functions into a single context value
  const contextValue = useMemo(
    () => ({
      products,
      product,
      cart, 
      collections,
      isCartOpen,
      cartCount,
      addItemsToCart,
      updateItemQuantity,
      removeItemFromCart,
      fetchAllProducts,
      fetchProductWithId,
      fetchProductsByType,
      searchProducts,
      closeCart,
      openCart,
      getCheckoutUrl,
      fetchProductByVariantId,
      market,
    }),
    [
      products,
      product,
      cart,
      collections,
      isCartOpen,
      cartCount,
      addItemsToCart,
      updateItemQuantity,
      removeItemFromCart,
      fetchAllProducts,
      fetchProductWithId,
      fetchProductsByType,
      searchProducts,
      closeCart,
      openCart,
      getCheckoutUrl,
      fetchProductByVariantId,
      market,
    ],
  );

  return (
    <ShopContext.Provider value={contextValue}>{children}</ShopContext.Provider>
  );
};

const ShopConsumer = ShopContext.Consumer;

export { ShopConsumer, ShopContext };
export default ShopProvider;
